import { Toast, ToastMessage } from 'primereact/toast';
import { createContext, useCallback, useContext, useRef } from 'react';

export type ToastContextProps = {
	showToast: (
		severity: ToastMessage['severity'],
		message: string,
		options?: Omit<ToastMessage, 'severity' | 'detail'>
	) => void;
};

export const ToastContext = createContext<ToastContextProps>({
	showToast: () => null,
});

export type ToastProviderProps = {
	children: React.ReactNode;
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
	// Members
	// ------------------------------------------------------------------------

	/** Reference to UI elements. */
	const ref = useRef<Toast>(null);

	// Methods
	// ------------------------------------------------------------------------

	/**
	 * Show a toast.
	 *
	 * @param severity The severity of the toast.
	 * @param message The message of the toast.
	 * @param options Any additional options.
	 */
	const showToast = useCallback(
		(
			severity: ToastMessage['severity'],
			message: string,
			options: Omit<ToastMessage, 'severity' | 'detail'> = {}
		) => {
			const { summary, ...otherOptions } = options;

			ref?.current?.show({
				severity,
				detail: message,
				summary: summary ? <div className="font-semibold mb-2">{summary}</div> : undefined,
				...otherOptions,
			});
		},
		[]
	);

	// Template
	// ------------------------------------------------------------------------

	return (
		<ToastContext.Provider value={{ showToast }}>
			<Toast ref={ref} position="bottom-left" />
			{children}
		</ToastContext.Provider>
	);
};

export const useToastContext = (): ToastContextProps => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error('useToastContext must be used within ToastProvider');
	}
	return context;
};
